import { ref, type Ref, reactive, computed, type ComputedRef } from 'vue';
import { merge, cloneDeep, isNil } from 'lodash';
import { graphql } from '@/gql';
import { useSwalDialog } from '@/use/swalDialog';

import util from '@/Utils/util';
import { useAddress } from '@/use/address';
import { queryExecute, mutationExecute } from '@/Utils/graphqlAPI';
import type { BlacklistInputType, BlacklistsQueryVariables } from '@/gql/graphql';

import { dateTimeFormat } from '@/constants/app';
import { defaultMapCenter } from '@/constants/address';
import { languagePreferenceList, hearAboutList } from '@/constants/caller';

import { CallerAdditionalInfoProps, FlaggedUserType, type Caller } from '@/models/caller';
import type { LovedOne } from '@/models/lovedOne';
import type { LocationDataType, CustomLocationType, SubsidyLocation } from '@/models/address';
import type { TotalOrderInfo } from '@/models/caller';
import { PartnerCodeName, SolanoCountyPartners, type PartnerRideAllowanceTier } from '@/models/partner';
import type { CallerPlan } from '@/models/callerPlan';
import type { BlacklistType, PaginatedBlacklistTypeResponse } from '@/models/blacklist';
import type { LeadWithHistoryType, Lead } from '@/models/lead';

import { callerService } from '@/services/caller';
import { sendCallerToExtension } from '@/services/extensionCaller';

const customerInfo = ref({} as any);
const leadInfo: Ref<LeadWithHistoryType | null> = ref(null);
const customerActivePlan: Ref<CallerPlan | null> = ref(null);
const customerPhoneNumber = ref('');
const lastRideInfo = ref({} as any);
const blacklistedInfo = ref(null as any);
const isUnpaidCustomer = ref(false);
const newCustomerInfo = ref({ phoneNumber: '' } as any);
const hasUnresolvedDisputes = ref(false);
const customerLovedOnes: Ref<LovedOne[]> = ref([]);
const customerPlanList = ref([] as any);
const hasActiveOrders = ref(false);
const hasCreditCard = ref(false);
const totalOrderInfo: Ref<TotalOrderInfo | null> = ref(null);
const isShownFirstDeliveryPopup = ref(false);
const isShownFirstGroceryPopup = ref(false);
const completedLastRideAddress: Ref<CustomLocationType | null> = ref(null);
const forceFetchCustomerData = ref<boolean>(false);
const newOperatorNote = ref('');
const customerNotesDialogVisible = ref(false);

export const useCustomer = () => {
  const { getFormattedAddress, makeAddressObject, getAddressObjectFromString } = useAddress();

  const customerFullName = computed(() => {
    const name = util.getFullName(customerInfo.value?.firstName, customerInfo.value?.lastName);

    if (customerInfo.value?.prefix) {
      return `${customerInfo.value.prefix} ${name}`;
    }

    return name;
  });

  const isOldCanadianUser = computed(() => {
    return !!customerInfo.value?.isOldCanadianUser;
  });

  const isAustralianUser = computed(() => customerInfo.value?.country?.toUpperCase() === 'AU');
  const isCanadianUser = computed(() => customerInfo.value?.country?.toUpperCase() === 'CA');

  const rideAvailablePhoneNumber = computed(
    () => customerInfo.value?.phoneNumber || customerInfo.value?.homeNumber || customerInfo.value?.phoneNumber2 || '',
  );

  const normalizePlanName = computed(() => util.normalizePlanName(customerActivePlan.value?.planName ?? ''));
  const customerPlans: {
    isGrandfatheredMember: boolean;
    isStandardMember: boolean;
    isGoldMember: boolean;
    isBasicMember: boolean;
    isBasicAnnualMember: boolean;
    isValueMember: boolean;
    isValueAnnualMember: boolean;
    isFixedIncomeMember: boolean;
    isFixedIncomeAnnualMember: boolean;
    isValueQuarterMember: boolean;
    isPremiumMember: boolean;
    isPremiumAnnualMember: boolean;
    isTotalCareMember: boolean;
    isTotalCareAnnualMember: boolean;
    isTrialPlan: boolean;
    isHighPlanMember: boolean;
    planDisplayName: string;
    planPrice: string;
  } = reactive({
    isGrandfatheredMember: computed(() => normalizePlanName.value === 'grandfathered'),
    isStandardMember: computed(() => normalizePlanName.value === 'GoGoStandard'),
    isGoldMember: computed(() => normalizePlanName.value === 'GoGoGold'),
    isBasicMember: computed(() => normalizePlanName.value === 'basic'),
    isBasicAnnualMember: computed(() => normalizePlanName.value === 'basicAnnual'),
    isValueMember: computed(() => normalizePlanName.value === 'value'),
    isValueAnnualMember: computed(() => normalizePlanName.value === 'valueAnnual'),
    isFixedIncomeMember: computed(() => normalizePlanName.value === 'fixedIncome'),
    isFixedIncomeAnnualMember: computed(() => normalizePlanName.value === 'fixedIncomeAnnual'),
    isValueQuarterMember: computed(() => normalizePlanName.value === 'valueQuarter'),
    isPremiumMember: computed(() => normalizePlanName.value === 'premium'),
    isPremiumAnnualMember: computed(() => normalizePlanName.value === 'premiumAnnual'),
    isTotalCareMember: computed(() => normalizePlanName.value === 'totalCare'),
    isTotalCareAnnualMember: computed(() => normalizePlanName.value === 'totalCareAnnual'),
    isTrialPlan: computed(() => customerActivePlan.value?.isTrial ?? false),
    isHighPlanMember: computed(
      () => customerPlans.isGoldMember || customerPlans.isValueMember || customerPlans.isPremiumMember || customerPlans.isTotalCareMember,
    ),
    planDisplayName: computed(() => {
      let planName: string;

      switch (normalizePlanName.value) {
        case 'GoGoStandard':
          planName = 'Standard Member';
          break;
        case 'GoGoGold':
          planName = 'Gold Member';
          break;
        case 'grandfathered':
          planName = 'Grandfathered Member';
          break;
        case 'basic':
          planName = 'Basic Member';
          break;
        case 'basicAnnual':
          planName = 'Annual Basic Member';
          break;
        case 'value':
          planName = 'Value Member';
          break;
        case 'valueAnnual':
          planName = 'Annual Value Member';
          break;
        case 'fixedIncome':
          planName = 'FixedIncome Member';
          break;
        case 'fixedIncomeAnnual':
          planName = 'Annual FixedIncome Member';
          break;
        case 'valueQuarter':
          planName = 'Quarter Value Member';
          break;
        case 'premium':
          planName = 'Premium Member';
          break;
        case 'premiumAnnual':
          planName = 'Annual Premium Member';
          break;
        case 'totalCare':
          planName = 'Total Care Member';
          break;
        case 'totalCareAnnual':
          planName = 'Annual Total Care Member';
          break;
        default:
          planName = normalizePlanName.value;
          break;
      }

      return planName;
    }),
    planPrice: computed(() => {
      if (!customerPlanList.value || !customerActivePlan.value) {
        return '';
      }

      const plan = customerPlanList.value.find((item: any) => item.value === customerActivePlan.value?.planName);

      return plan?.price;
    }),
  });

  const customerStatus: {
    isPartnerCustomer: boolean;
    isMembershipExpired: boolean;
    isMembershipSuspended: boolean;
    isTakenPaidMonthlyPlan: boolean;
    isInFirstMonthOfHardRegister: boolean;
    isFraudCustomer: boolean;
    isNeedsToClearDisputesCustomer: boolean;
    isBusinessAccount: boolean;
    isBlacklisted: boolean;
    isUnpaidCustomer: boolean;
    isActiveCustomer: boolean;
    isNeverHadMembership: boolean;
    hasUnresolvedDisputes: boolean;
    hasActiveOrders: boolean;
    hasCreditCard: boolean;
    isAffiliateCaller: boolean;
    hasActiveRide: boolean;
    isAutoRetryGettingEstimate: boolean;
  } = reactive({
    isPartnerCustomer: computed(() => !!customerInfo.value?.isOnPartnership),
    isMembershipExpired: computed(() => !!customerInfo.value?.isMembershipExpired),
    isTakenPaidMonthlyPlan: computed(() => !!customerInfo.value?.isTakenPaidMonthlyPlan),
    isInFirstMonthOfHardRegister: computed(() => !!customerInfo.value?.isInFirstMonthOfHardRegister),
    isMembershipSuspended: computed(() => !!customerInfo.value?.isMembershipSuspended),
    isFraudCustomer: computed(() => customerInfo.value && customerInfo.value.flagged === FlaggedUserType.FraudUser),
    isNeedsToClearDisputesCustomer: computed(
      () => customerInfo.value && customerInfo.value.flagged === FlaggedUserType.NeedsToClearDisputes,
    ),
    isBusinessAccount: computed(() => !!customerInfo.value?.isBusinessAccount),
    isBlacklisted: computed(() => !!blacklistedInfo.value),
    isUnpaidCustomer: computed(() => isUnpaidCustomer.value),
    isActiveCustomer: computed(() => !!customerInfo.value?.isActive),
    isNeverHadMembership: computed(() => !!customerInfo.value?.isNeverHadMembership),
    hasUnresolvedDisputes: computed(() => hasUnresolvedDisputes.value),
    hasActiveOrders: computed(() => hasActiveOrders.value),
    hasCreditCard: computed(() => hasCreditCard.value),
    isAffiliateCaller: computed(() => !!customerInfo.value?.isAffiliateCaller),
    hasActiveRide: computed(() => customerInfo.value.activeRides && customerInfo.value.activeRides.length > 0),
    isAutoRetryGettingEstimate: computed(() => !!customerInfo.value?.isAutoRetryGettingEstimate),
  });

  const customerPartner = computed(() => customerInfo.value?.partner);
  const customerPartnerCode = computed(() => customerInfo.value?.partner?.codeName);
  const isScheduledRideRestricted = computed(() => Boolean(customerPartner.value?.isScheduledRideRestricted));

  const customerPartnerId = computed(() => customerInfo.value?.partner?.id);
  const customerPartnerSubsidyLocations: ComputedRef<SubsidyLocation[]> = computed(
    () => customerInfo.value?.partner?.subsidyLocations ?? [],
  );
  const customerPartnerRideAllowanceTiers = computed(() => {
    return (
      customerInfo.value?.partner?.rideAllowance?.tiers?.map((item: PartnerRideAllowanceTier) => ({
        id: item.name,
        key: item.name,
        label: item.name !== 'custom' ? `${item.allowance} rides - ${item.name}` : item.name,
      })) ?? []
    );
  });

  /** When the only configured tier is "custom", hide the dropdown and silently use custom. */
  const isOnlyCustomRideAllowanceTier = computed(() => {
    const tiers = customerPartnerRideAllowanceTiers.value;
    return tiers.length === 1 && tiers[0].key === 'custom';
  });

  const availablePartnerRidePurposes = computed(() => {
    const ridePurposes = customerInfo.value?.partner?.rideOptions?.partnerRidePurposes || [];
    return ridePurposes.map((purpose: string, idx: number) => ({
      id: idx + 1,
      key: purpose,
      label: purpose,
    }));
  });

  const isDutchessPartnership = computed(() => customerPartnerCode?.value === 'dutchess');
  const isSolanoCountyPartnership = computed(() => SolanoCountyPartners.includes(customerPartnerCode?.value));
  const isDiamondBarPartnership = computed(() => customerPartnerCode?.value === PartnerCodeName.DIAMOND_BAR);
  // demo mirrors diamondBar's banner for testing
  const isDiamondBarSuspendedBannerPartner = computed(() =>
    [PartnerCodeName.DIAMOND_BAR, PartnerCodeName.DEMO].includes(customerPartnerCode?.value),
  );
  const isRotcPartnership = computed(() => customerPartnerCode?.value === 'rotc' || customerPartnerCode?.value === 'rotcgo');

  const isHideRideEstimateFullDetail = computed(() => {
    // list of partners that should not show ride estimate full detail
    const partners = [PartnerCodeName.UMASS, PartnerCodeName.UMASSNICU, PartnerCodeName.ACRC, PartnerCodeName.ORIONISO];
    return partners.includes(customerPartnerCode?.value);
  });

  const isDisableDeliveryServiceInterestedPopup = computed(() => {
    // list of partners that should not show delivery service interested popup
    const partners = [PartnerCodeName.UMASS, PartnerCodeName.UMASSNICU];
    return partners.includes(customerPartnerCode?.value);
  });

  const anchorBound = computed(() => {
    if (!window.google) {
      return;
    }

    const circle = new google.maps.Circle({
      center: {
        lat: customerInfo.value?.latitude || defaultMapCenter.lat,
        lng: customerInfo.value?.longitude || defaultMapCenter.lng,
      },
      radius: 100,
    });

    return circle.getBounds();
  });

  const isNeedToShowFirstDeliveryPopup = computed(() => {
    return totalOrderInfo.value?.totalGourmets === 0;
  });

  const isNeedToShowFirstGroceryPopup = computed(() => {
    return totalOrderInfo.value?.totalGroceries === 0;
  });

  const contactPhoneNumbers = computed(() => {
    const list = [];

    if (customerInfo.value.phoneNumber) {
      list.push({
        id: 'phoneNumber',
        key: customerInfo.value.phoneNumber,
        label: `Cell Phone - ${customerInfo.value.phoneNumber}`,
      });
    }

    if (customerInfo.value.phoneNumber2) {
      list.push({
        id: 'otherPhone',
        key: customerInfo.value.phoneNumber2,
        label: `Other Phone - ${customerInfo.value.phoneNumber2}`,
      });
    }

    if (customerInfo.value.homeNumber) {
      list.push({
        id: 'homeNumber',
        key: customerInfo.value.homeNumber,
        label: `Other Phone - ${customerInfo.value.homeNumber}`,
      });
    }

    list.push({
      id: 'newPhoneNumber',
      key: '',
      label: 'New Phone Number',
    });

    return list;
  });

  const addressList = computed(() => {
    const list = [];

    list.push({
      id: 'home',
      key: 'home',
      label: 'Home Address',
      address: {
        ...makeAddressObject(customerInfo.value),
        name: customerInfo.value.customHomeName || 'Home',
      },
    });

    if (completedLastRideAddress.value) {
      const fullAddress = getFormattedAddress(completedLastRideAddress.value);

      list.push({
        id: 'lastRideLocation',
        key: 'lastRideLocation',
        label: `Last Ride Location - ${fullAddress}`,
        address: makeAddressObject(completedLastRideAddress.value),
      });
    }

    const customLocationKeys = ['a', 'b', 'c'];

    for (const loc of customLocationKeys) {
      if (!customerInfo.value[loc + 'Location']) {
        continue;
      }

      const fullAddress = customerInfo.value[loc + 'Location'];
      const address = getAddressObjectFromString(customerInfo.value[loc + 'Location']) as LocationDataType;

      list.push({
        id: loc + 'Location',
        key: loc + 'Location',
        label: customerInfo.value[loc + 'Name'] ? `${customerInfo.value[loc + 'Name']} - ${fullAddress}` : fullAddress,
        address: {
          ...address,
          latitude: Number(customerInfo.value[loc + 'Lat']),
          longitude: Number(customerInfo.value[loc + 'Long']),
          name: customerInfo.value[loc + 'Name'],
        },
      });
    }

    const customAddresses = customerInfo.value.customAddresses || [];

    for (const customAddress of customAddresses) {
      const address = makeAddressObject(customAddress);
      const fullAddress = getFormattedAddress(address);
      list.push({
        id: `customAddress${customAddress.order}`,
        key: `customAddress${customAddress.order}`,
        label: customAddress.name ? `${customAddress.name} - ${fullAddress}` : fullAddress,
        address,
      });
    }

    list.push({
      id: 'newLocation',
      key: '',
      label: 'New Address',
    });

    return list;
  });

  // We have to exclude Membership page for partnership user in client dashboard
  // It can be extended and need to make the computed variable for consistency
  const excludedPages = computed(() => {
    if (!customerPartnerId.value) {
      return [];
    }

    return ['/family/membership-plan'];
  });

  const isAllowedToExpandVehicleSearch = computed(() => !customerInfo.value?.isNoBigCars && !customerInfo.value?.isNoSmallCars);

  const setCustomerInfo = (value: any) => {
    customerInfo.value = cloneDeep(value);
    sendCallerToExtension(customerInfo.value);
  };

  const setLeadInfo = (value: LeadWithHistoryType | null) => {
    leadInfo.value = cloneDeep(value);
  };

  const setCustomerPhoneNumber = (phoneNumber: string) => {
    customerPhoneNumber.value = phoneNumber;
  };

  const setCustomerActivePlan = (plan: CallerPlan | null) => {
    customerActivePlan.value = cloneDeep(plan);
  };

  const setCustomerPlanList = (planList: any) => {
    customerPlanList.value = planList;
  };

  const setCustomerLastRideInfo = (rideInfo: any) => {
    lastRideInfo.value = cloneDeep(rideInfo);
  };

  const setBlacklistedInfo = (value: any) => {
    blacklistedInfo.value = value;
  };

  const setUnpaidUser = (value: boolean) => {
    isUnpaidCustomer.value = value;
  };

  const setNewCustomerInfo = (value: any) => {
    newCustomerInfo.value = cloneDeep(value);
  };

  const setHasUnresolvedDisputes = (value: boolean) => {
    hasUnresolvedDisputes.value = value;
  };

  const setHasActiveOrders = (value: boolean) => {
    hasActiveOrders.value = value;
  };

  const setHasCreditCard = (value: boolean) => {
    hasCreditCard.value = value;
  };

  const setTotalOrderInfo = (value: TotalOrderInfo | null) => {
    totalOrderInfo.value = value;
  };

  const setShownFirstDeliveryPopup = (value: boolean) => {
    isShownFirstDeliveryPopup.value = value;
  };

  const setShownFirstGroceryPopup = (value: boolean) => {
    isShownFirstGroceryPopup.value = value;
  };

  const setForceFetchCustomerData = (val: boolean) => (forceFetchCustomerData.value = val);

  const formatCustomerInfo = (customer: any) => {
    const name = util.getFullName(customer.prefix, util.getFullName(customer.firstName, customer.lastName));
    customer = merge({ name }, customer);

    customer.createdAt = util.formatDateTime(customer.createdAt, dateTimeFormat, customer.timeZone);
    customer.language1 = customer.language1?.toUpperCase() || '';
    customer.language2 = customer.language2?.toUpperCase() || '';
    customer.credits = customer.credits ? `$${customer.credits}` : '';
    customer.partnerCredits = customer.partnerCredits ? `$${customer.partnerCredits}` : '';

    customer.latitude = customer.latitude ? Number.parseFloat(customer.latitude).toFixed(6) : null;
    customer.longitude = customer.longitude ? Number.parseFloat(customer.longitude).toFixed(6) : null;

    customer.aLat = customer.aLat ? Number.parseFloat(customer.aLat).toFixed(6) : null;
    customer.aLong = customer.aLong ? Number.parseFloat(customer.aLong).toFixed(6) : null;

    customer.bLat = customer.bLat ? Number.parseFloat(customer.bLat).toFixed(6) : null;
    customer.bLong = customer.bLong ? Number.parseFloat(customer.bLong).toFixed(6) : null;

    customer.cLat = customer.cLat ? Number.parseFloat(customer.cLat).toFixed(6) : null;
    customer.cLong = customer.cLong ? Number.parseFloat(customer.cLong).toFixed(6) : null;

    customer.langPrefForUI = util.getLabelByField(languagePreferenceList, customer.langPrefForUI, 'id');
    customer.langPrefForOperator = util.getLabelByField(languagePreferenceList, customer.langPrefForOperator, 'id');
    customer.langPrefForDriver = util.getLabelByField(languagePreferenceList, customer.anguagePreferenceForDriver, 'id');
    customer.hearAboutUs = util.getLabelByField(hearAboutList, customer.hearAboutUs);

    customer.familyName = util.getFullName(customer.familyFirstName, customer.familyLastName);
    for (const key in customer) {
      customer[key] = customer[key] === true ? 'Yes' : customer[key] === false ? 'No' : customer[key];
    }

    delete customer.firstName;
    delete customer.lastName;
    delete customer.prefix;
    delete customer.familyFirstName;
    delete customer.familyLastName;

    return customer;
  };

  const setCustomerLovedOnes = (lovedOnes: LovedOne[]) => {
    customerLovedOnes.value = cloneDeep(lovedOnes);
  };

  const setCompletedLastRideAddress = (address: CustomLocationType | null) => {
    completedLastRideAddress.value = cloneDeep(address);
  };

  const checkDuplicateCustomLocation = (address: CustomLocationType) => {
    // check with home address
    if (
      customerInfo.value.streetAddress === address.streetAddress &&
      customerInfo.value.city === address.city &&
      customerInfo.value.state === address.state &&
      customerInfo.value.zip === address.zip
    ) {
      throw new Error(
        `Looks like "${getFormattedAddress(
          address,
        )}" is already set as your Home address, we can't add this address again or else it'll be a duplicate!`,
      );
    }

    const strInds = ['a', 'b', 'c'];
    for (let index = 0; index < 3; index++) {
      if (index === address.index) {
        continue;
      }

      if (!customerInfo.value[`${strInds[index]}Location`]) {
        continue;
      }

      const customLocation = getAddressObjectFromString(customerInfo.value[`${strInds[index]}Location`]);
      if (
        customLocation.streetAddress === address.streetAddress &&
        customLocation.city === address.city &&
        customLocation.state === address.state &&
        customLocation.zip === address.zip
      ) {
        throw new Error(
          `Looks like "${getFormattedAddress(address)}" is already set as your Custom Location #${
            index + 3
          }, we can't add this address again or else it'll be a duplicate!`,
        );
      }
    }

    const customAddresses = customerInfo.value.customAddresses || [];

    for (const customAddress of customAddresses) {
      if (customAddress.id === address.id) {
        continue;
      }

      if (
        customAddress.streetAddress === address.streetAddress &&
        customAddress.city === address.city &&
        customAddress.state === address.state &&
        customAddress.zip === address.zip
      ) {
        throw new Error(
          `Looks like "${getFormattedAddress(address)}" is already set as your Custom Location *${
            customAddress.order
          }, we can't add this address again or else it'll be a duplicate!`,
        );
      }
    }
  };

  const updateUserPassword = async (userId: string, password: string) => {
    return mutationExecute({
      mutation: graphql(`
        mutation ChangeUserPassword($userId: String!, $password: String!) {
          changeUserPassword(userId: $userId, password: $password)
        }
      `),
      variables: {
        userId,
        password,
      },
    });
  };

  const fetchBlacklists = async ({
    firstName,
    lastName,
    query,
    type,
    pagination,
  }: BlacklistsQueryVariables): Promise<PaginatedBlacklistTypeResponse> => {
    const response = await queryExecute({
      query: graphql(`
        query Blacklists($firstName: String, $lastName: String, $query: String, $type: String!, $pagination: PaginationInputType) {
          blacklists(firstName: $firstName, lastName: $lastName, query: $query, type: $type, pagination: $pagination) {
            count
            rows {
              id
              firstName
              lastName
              callerId
              active
              phoneNumber
              notes
              reporterName
              services
            }
          }
        }
      `),
      variables: {
        firstName,
        lastName,
        query,
        type,
        pagination,
      },
      fetchPolicy: 'no-cache',
    });

    return response.data.blacklists;
  };

  const createBlacklist = async (data: BlacklistInputType): Promise<BlacklistType> => {
    const response = await mutationExecute({
      mutation: graphql(`
        mutation CreateBlackList($data: BlacklistInputType!) {
          createBlacklist(data: $data) {
            id
          }
        }
      `),
      variables: {
        data,
      },
    });

    return response.data?.createBlacklist!;
  };

  const updateBlacklist = async ({ id, data }: { id: number; data: BlacklistInputType }): Promise<BlacklistType> => {
    const response = await mutationExecute({
      mutation: graphql(`
        mutation UpdateBlacklistMutation($data: BlacklistInputType!, $id: Float!) {
          updateBlacklist(data: $data, id: $id) {
            id
          }
        }
      `),
      variables: {
        data,
        id,
      },
    });

    return response.data?.updateBlacklist!;
  };

  const deleteBlacklist = async (id: number): Promise<BlacklistType> => {
    const response = await mutationExecute({
      mutation: graphql(`
        mutation DeleteBlacklistMutation($id: Float!) {
          deleteBlacklist(id: $id) {
            id
          }
        }
      `),
      variables: {
        id,
      },
    });

    return response.data?.deleteBlacklist!;
  };

  const add100MilesNoteToOperatorNote = (val: string) => {
    newOperatorNote.value = val;
  };

  const getFormattedPlanList = (hasSpecialPermission: boolean = false) => {
    return computed(() => {
      const list = customerPlanList.value.map((item: any) => {
        let label = `${item.name} ${item.price || ''}`;

        if (item.isTrial) {
          label = `${item.name} Trial (${item.trialPrice || ''} instead of ${item.price || ''})`;
        }

        return {
          ...item,
          key: item.value || '',
          label,
        };
      });

      if (!hasSpecialPermission) {
        return list;
      }

      return [{ id: 1, key: '', label: 'None Member' }, ...list];
    });
  };

  const setCustomerNotesDialogVisible = (value: boolean) => {
    customerNotesDialogVisible.value = value;
  };

  const checkAndShowAdaDialog = async () => {
    if (!customerInfo.value) {
      return;
    }

    const { showConfirmPopupDialog } = useSwalDialog();

    const FLAGS = ['isWheelchair', 'isVisuallyImpaired', 'isOxygenTank', 'isServiceDog', 'isWalker', 'isCane', 'isPowerWheelchairScooter'];
    if (isNil(customerInfo.value.isAda) && FLAGS.some((flag) => customerInfo.value[flag])) {
      const isAda = await showConfirmPopupDialog({
        cancelButtonText: 'No',
        confirmButtonText: 'Yes',
        text: 'Ask the customer: do they have a disability protected by the Americans with Disabilities Act (ADA)?',
        title: 'ADA',
      });
      await callerService.updateCaller({
        callerId: customerInfo.value.id,
        data: { isAda },
      });
      setCustomerInfo({
        ...customerInfo.value,
        isAda,
      });
    }
  };

  const prefillCustomerInfoFromLeadCallerData = (lead: Lead | null, leadCaller: Caller | null) => {
    newCustomerInfo.value = {
      ...leadCaller,
      phoneNumber: leadCaller?.phoneNumber ?? lead?.phoneNumber ?? '',
      firstName: leadCaller?.firstName ?? lead?.firstName ?? '',
      lastName: leadCaller?.lastName ?? lead?.lastName ?? '',
      streetAddress: leadCaller?.streetAddress ?? lead?.streetAddress ?? '',
      city: leadCaller?.city ?? lead?.city ?? '',
      state: leadCaller?.state ?? lead?.state ?? '',
      zip: leadCaller?.zip ?? lead?.zip ?? '',
      latitude: leadCaller?.latitude ?? lead?.latitude ?? null,
      longitude: leadCaller?.longitude ?? lead?.longitude ?? null,
    };

    if (newCustomerInfo.value.dob) {
      const dobDates = newCustomerInfo.value.dob.split('-');
      newCustomerInfo.value.dobYear = Number(dobDates[0]);
      newCustomerInfo.value.dobMonth = Number(dobDates[1]);
      newCustomerInfo.value.dobDay = Number(dobDates[2]);
    }

    for (const prop of CallerAdditionalInfoProps) {
      if (prop === 'noAdditionalInfo') {
        continue;
      }

      if (newCustomerInfo.value[prop]) {
        newCustomerInfo.value.noAdditionalInfo = false;
        break;
      }
    }
  };

  return {
    customerInfo,
    leadInfo,
    customerPhoneNumber,
    customerFullName,
    customerPlans,
    customerStatus,
    customerPartner,
    customerPartnerCode,
    customerPartnerId,
    isScheduledRideRestricted,
    customerPlanList,
    customerActivePlan,
    anchorBound,
    lastRideInfo,
    rideAvailablePhoneNumber,
    newCustomerInfo,
    customerLovedOnes,
    isNeedToShowFirstDeliveryPopup,
    isShownFirstDeliveryPopup,
    isNeedToShowFirstGroceryPopup,
    isShownFirstGroceryPopup,
    contactPhoneNumbers,
    addressList,
    totalOrderInfo,
    isOldCanadianUser,
    isAustralianUser,
    isCanadianUser,
    isHideRideEstimateFullDetail,
    isDisableDeliveryServiceInterestedPopup,
    isDutchessPartnership,
    blacklistedInfo,
    excludedPages,
    forceFetchCustomerData,
    newOperatorNote,
    isSolanoCountyPartnership,
    isAllowedToExpandVehicleSearch,
    isDiamondBarPartnership,
    isDiamondBarSuspendedBannerPartner,
    isRotcPartnership,
    customerPartnerSubsidyLocations,
    customerPartnerRideAllowanceTiers,
    isOnlyCustomRideAllowanceTier,
    customerNotesDialogVisible,
    availablePartnerRidePurposes,

    formatCustomerInfo,
    checkDuplicateCustomLocation,
    setCustomerInfo,
    setLeadInfo,
    setCustomerActivePlan,
    setCustomerPhoneNumber,
    setCustomerLastRideInfo,
    setBlacklistedInfo,
    setUnpaidUser,
    updateUserPassword,
    fetchBlacklists,
    createBlacklist,
    updateBlacklist,
    deleteBlacklist,
    setNewCustomerInfo,
    setHasUnresolvedDisputes,
    setHasActiveOrders,
    setCustomerLovedOnes,
    setCustomerPlanList,
    setHasCreditCard,
    setTotalOrderInfo,
    setShownFirstDeliveryPopup,
    setShownFirstGroceryPopup,
    setCompletedLastRideAddress,
    setForceFetchCustomerData,
    add100MilesNoteToOperatorNote,
    getFormattedPlanList,
    setCustomerNotesDialogVisible,
    checkAndShowAdaDialog,
    prefillCustomerInfoFromLeadCallerData,
  };
};

// AB Test: simple ride ordering - shared ref set during dashboard init
export const isSimpleRideOrderingTest = ref(false);
